import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Paper,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  DialogContentText,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Typography,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import { getUsers, assignAccessGroupsToUser, User } from 'services/user';
import api from 'services/api';
import DataTable from 'components/Shared/Table';
import Loader from 'components/Shared/Loader';
import type { MUIDataTableState } from 'mui-datatables';
import { primary } from 'helpers/colors';

type Props = {
  accessGroupId: number;
  accessGroupName: string;
  onRefresh: () => void;
};

interface AccessGroupUser extends User {
  id: number;
  name: string;
  email: string;
  first_login: boolean;
  profile?: {
    id: number;
    name: string;
  } | null;
}

interface GroupProfile {
  id: number;
  name: string;
}

function UsersTab({ accessGroupId, accessGroupName, onRefresh }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState<AccessGroupUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [checkedUserIds, setCheckedUserIds] = useState<number[]>([]);
  const [modalSearch, setModalSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [userToRemove, setUserToRemove] = useState<number | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [userToEditProfile, setUserToEditProfile] = useState<AccessGroupUser | null>(null);
  const [groupProfiles, setGroupProfiles] = useState<GroupProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<number | ''>('');
  const [isFetchingProfiles, setIsFetchingProfiles] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<{ data: { users: AccessGroupUser[]; total: number } }>(
        `/access_groups/${accessGroupId}/users`,
        {
          perPage: rowsPerPage,
          page: page,
          search: searchText,
          order: sortField,
          direction: sortDirection
        }
      );

      if (response.data?.users) {
        setUsers(response.data.users);
        setTotalCount(response.data?.total || 0);
      }
      setIsLoading(false);
    } catch (error) {
      enqueueSnackbar('Erro ao carregar usuários', { variant: 'error' });
      setIsLoading(false);
    }
  }, [accessGroupId, enqueueSnackbar, page, rowsPerPage, searchText, sortField, sortDirection]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const fetchAvailableUsers = useCallback(
    (q: string) => {
      setIsFetching(true);
      getUsers(
        data => {
          const filtered = data.filter(u => !users.some(eu => eu.id === u.id));
          setAvailableUsers(filtered);
          setIsFetching(false);
        },
        { perPage: 50, page: 1, search: q || '' }
      );
    },
    [users]
  );

  useEffect(() => {
    if (!addModalOpen) return undefined;
    const handler = window.setTimeout(() => fetchAvailableUsers(modalSearch), 400);
    return () => clearTimeout(handler);
  }, [modalSearch, addModalOpen, fetchAvailableUsers]);

  const handleAddClick = () => {
    setModalSearch('');
    setCheckedUserIds([]);
    fetchAvailableUsers('');
    setAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setAddModalOpen(false);
    setModalSearch('');
    setCheckedUserIds([]);
  };

  const handleToggleUser = (userId: number) => {
    setCheckedUserIds(prev => (prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]));
  };

  const handleConfirmAdd = () => {
    if (checkedUserIds.length === 0) return;
    setIsAdding(true);
    assignAccessGroupsToUser(
      accessGroupId,
      checkedUserIds,
      () => {
        setIsAdding(false);
        enqueueSnackbar('Usuários adicionados com sucesso', { variant: 'success' });
        handleCloseModal();
        loadUsers();
        onRefresh();
      },
      errorMsg => {
        setIsAdding(false);
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    );
  };

  const handleRemoveUser = useCallback((userId: number) => {
    setUserToRemove(userId);
  }, []);

  const confirmRemoveUser = useCallback(async () => {
    if (userToRemove === null) return;
    setIsRemoving(true);
    try {
      await api.delete(`/access_groups/${accessGroupId}/users/${userToRemove}`);
      enqueueSnackbar('Usuário removido com sucesso', { variant: 'success' });
      setUserToRemove(null);
      loadUsers();
      onRefresh();
    } catch (error) {
      enqueueSnackbar('Erro ao remover usuário', { variant: 'error' });
    } finally {
      setIsRemoving(false);
    }
  }, [userToRemove, accessGroupId, enqueueSnackbar, loadUsers, onRefresh]);

  const handleEditProfileClick = useCallback(
    async (user: AccessGroupUser) => {
      setUserToEditProfile(user);
      setSelectedProfileId(user.profile?.id ?? '');
      setIsFetchingProfiles(true);
      try {
        const response = await api.get<any>(`/access_groups/${accessGroupId}/profiles`, {
          perPage: 9999,
          page: 1
        });
        const list: GroupProfile[] = response.data?.data || response.data || [];
        setGroupProfiles(Array.isArray(list) ? list : []);
      } catch {
        enqueueSnackbar('Erro ao carregar perfis', { variant: 'error' });
      } finally {
        setIsFetchingProfiles(false);
      }
    },
    [accessGroupId, enqueueSnackbar]
  );

  const handleCloseEditProfile = () => {
    setUserToEditProfile(null);
    setGroupProfiles([]);
    setSelectedProfileId('');
  };

  const handleConfirmEditProfile = useCallback(async () => {
    if (!userToEditProfile || selectedProfileId === '') return;
    setIsUpdatingProfile(true);
    try {
      await api.put(`/access_groups/${accessGroupId}/users/${userToEditProfile.id}`, {
        profile_id: selectedProfileId
      });
      enqueueSnackbar('Perfil do usuário atualizado com sucesso', { variant: 'success' });
      handleCloseEditProfile();
      loadUsers();
    } catch {
      enqueueSnackbar('Erro ao atualizar perfil do usuário', { variant: 'error' });
    } finally {
      setIsUpdatingProfile(false);
    }
  }, [userToEditProfile, selectedProfileId, accessGroupId, enqueueSnackbar, loadUsers]);

  const formattedData = useMemo(() => [...users], [users]);

  const renderActions = useCallback(
    (_value: number, tableMeta: any) => {
      const user = users[tableMeta.rowIndex];
      if (!user) return null;
      return (
        <Box display='flex' gap={0.5}>
          <Tooltip title='Alterar perfil'>
            <IconButton size='small' color='primary' onClick={() => handleEditProfileClick(user)}>
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Remover'>
            <IconButton size='small' color='error' onClick={() => handleRemoveUser(user.id)}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
    [users, handleEditProfileClick, handleRemoveUser]
  );

  const columns = useMemo(
    () => [
      {
        name: 'name',
        label: 'Nome',
        options: {
          sort: true,
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'email',
        label: 'E-mail',
        options: {
          sort: true,
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'first_login',
        label: 'Primeiro login',
        options: {
          sort: false,
          customBodyRender: (value: boolean) => (value ? 'Sim' : 'Não'),
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'profile',
        label: 'Perfil',
        options: {
          sort: false,
          customBodyRender: (value: { id: number; name: string } | null) => value?.name || 'Sem perfil',
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      },
      {
        name: 'id',
        label: 'Ações',
        options: {
          sort: false,
          customBodyRender: renderActions,
          setCellHeaderProps: (): object => ({ style: { textAlign: 'left' } }),
          setCellProps: (): object => ({ style: { textAlign: 'left' } })
        }
      }
    ],
    [renderActions]
  );

  const onChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const onChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const onSearch = (search: string) => {
    setSearchText(search);
    setPage(1);
  };

  const onSortTable = (column: string, direction: 'asc' | 'desc') => {
    setSortField(column);
    setSortDirection(direction);
    setPage(1);
  };

  const handleTableChange = useCallback((action: string, tableState: MUIDataTableState) => {
    switch (action) {
      case 'changePage':
        onChangePage(tableState.page + 1);
        break;
      case 'changeRowsPerPage':
        onChangeRowsPerPage(tableState.rowsPerPage);
        break;
      case 'search':
        onSearch(tableState.searchText || '');
        break;
      case 'sort':
        if (tableState.sortOrder && tableState.sortOrder.name && tableState.sortOrder.direction) {
          onSortTable(tableState.sortOrder.name, tableState.sortOrder.direction);
        }
        break;
      default:
        console.info(`action (${action}) not handled.`);
    }
  }, []);

  const optionsObj = useMemo(
    () => ({
      page: page - 1,
      count: totalCount,
      rowsPerPage,
      jumpToPage: true,
      onTableChange: handleTableChange,
      serverSide: true,
      search: false,
      searchText,
      selectableRows: 'none',
      sortOrder: { name: sortField, direction: sortDirection }
    }),
    [page, totalCount, rowsPerPage, handleTableChange, searchText, sortField, sortDirection]
  );

  return (
    <Box>
      <Box mb={0} display='flex' mx={2} gap={2} alignItems='center'>
        <TextField
          value={searchText}
          onChange={e => {
            setSearchText(e.target.value);
            setPage(1);
          }}
          placeholder='Pesquisar por Nome ou E-mail'
          variant='outlined'
          size='small'
          sx={{ input: { fontSize: '0.9rem' }, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 10 } }}
        />
        <Button
          variant='contained'
          color='primary'
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disableElevation
          sx={{ borderRadius: 10, whiteSpace: 'nowrap', py: 0.7, '&:hover': { backgroundColor: primary.hover } }}
        >
          Adicionar Usuário
        </Button>
      </Box>

      <Card elevation={0}>
        <CardContent>
          <TableContainer elevation={0} component={Paper}>
            {isLoading ? <Loader /> : <DataTable columns={columns} data={formattedData} optionsObj={optionsObj} />}
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={addModalOpen} onClose={!isAdding ? handleCloseModal : undefined} maxWidth='sm' fullWidth>
        <DialogTitle>Adicionar Usuários</DialogTitle>
        <DialogContent dividers>
          <Box>
            <TextField
              fullWidth
              size='small'
              placeholder='Buscar por nome ou e-mail'
              value={modalSearch}
              onChange={e => setModalSearch(e.target.value)}
              disabled={isAdding}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                )
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 10 } }}
            />
            <Box mt={1.5} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              {isFetching && (
                <Box display='flex' justifyContent='center' py={4}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!isFetching && availableUsers.length === 0 && (
                <Box py={4} textAlign='center'>
                  <Typography variant='body2' color='text.secondary'>
                    Nenhum usuário disponível
                  </Typography>
                </Box>
              )}
              {!isFetching && availableUsers.length > 0 && (
                <List dense disablePadding sx={{ maxHeight: 320, overflow: 'auto' }}>
                  {availableUsers.map(user => (
                    <ListItemButton
                      key={user.id}
                      onClick={() => handleToggleUser(user.id)}
                      disabled={isAdding}
                      sx={{ px: 1.5 }}
                    >
                      <Checkbox
                        edge='start'
                        checked={checkedUserIds.includes(user.id)}
                        tabIndex={-1}
                        disableRipple
                        size='small'
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={user.name}
                        secondary={user.email}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
            {checkedUserIds.length > 0 && (
              <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
                {checkedUserIds.length} usuário{checkedUserIds.length > 1 ? 's' : ''} selecionado
                {checkedUserIds.length > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '14px 20px' }}>
          <Button onClick={handleCloseModal} disabled={isAdding}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleConfirmAdd}
            disabled={isAdding || checkedUserIds.length === 0}
            disableElevation
            startIcon={isAdding ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ borderRadius: 10, whiteSpace: 'nowrap', py: 0.7, '&:hover': { backgroundColor: primary.hover } }}
          >
            {isAdding ? 'Salvando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={userToEditProfile !== null}
        onClose={!isUpdatingProfile ? handleCloseEditProfile : undefined}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Editar perfil de {userToEditProfile?.name}</DialogTitle>
        <DialogContent dividers>
          {isFetchingProfiles ? (
            <Box display='flex' justifyContent='center' py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box>
              <FormControl fullWidth size='small'>
                <InputLabel id='profile-select-label'>Perfil</InputLabel>
                <Select
                  labelId='profile-select-label'
                  value={selectedProfileId}
                  size='small'
                  label='Perfil'
                  onChange={e => setSelectedProfileId(e.target.value as number)}
                  disabled={isUpdatingProfile}
                >
                  {groupProfiles.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ paddingTop: 5, marginBottom: 6, marginRight: 12 }}>
          <Button onClick={handleCloseEditProfile} disabled={isUpdatingProfile} sx={{ mt: 1 }}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleConfirmEditProfile}
            disabled={isUpdatingProfile || selectedProfileId === '' || isFetchingProfiles}
            disableElevation
            startIcon={isUpdatingProfile ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{
              borderRadius: 10,
              whiteSpace: 'nowrap',
              py: 0.7,
              mt: 1,
              '&:hover': { backgroundColor: primary.hover }
            }}
          >
            {isUpdatingProfile ? 'Salvando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={userToRemove !== null} onClose={() => !isRemoving && setUserToRemove(null)}>
        <DialogTitle>Remover usuário</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover{' '}
            <strong>{users.find(u => u.id === userToRemove)?.name ?? 'este usuário'}</strong> do grupo{' '}
            <strong>{accessGroupName}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ paddingTop: 5, marginBottom: 10, marginRight: 12 }}>
          <Button onClick={() => setUserToRemove(null)} disabled={isRemoving}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={confirmRemoveUser}
            disabled={isRemoving}
            disableElevation
            startIcon={isRemoving ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ borderRadius: 10, whiteSpace: 'nowrap' }}
          >
            {isRemoving ? 'Removendo...' : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UsersTab;
